/**
 * Domain types for boerenbridge (a.k.a. Oh Hell / Up and Down the River).
 *
 * Identifiers are English so they read like the rest of the code; every
 * user-facing label is resolved through i18n, so suits appear as
 * schoppen/harten/ruiten/klaveren in the UI.
 */

export type Suit = 'spades' | 'hearts' | 'diamonds' | 'clubs';

export const SUITS: readonly Suit[] = ['spades', 'hearts', 'diamonds', 'clubs'];

/** 2–10 plus jack 11, queen 12, king 13, ace 14 (ace high). */
export type Rank = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14;

export const RANKS: readonly Rank[] = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

export interface Card {
  suit: Suit;
  rank: Rank;
}

/** `null` means the round is played without trump ("troefloos"). */
export type TrumpSuit = Suit | null;

export interface PlayedCard {
  seat: number;
  card: Card;
}

/** Cards played to the current trick, in play order — the leader is index 0. */
export type Trick = PlayedCard[];

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * How a round is scored. Stored per game so that changing the house rules
 * later never retroactively rewrites the score of an old game.
 */
export interface ScoringConfig {
  /** Points for calling your bid exactly. */
  exactBase: number;
  /** Extra points per bid trick, on top of `exactBase`, when exact. */
  perTrickBid: number;
  /**
   * Points per bid *squared* when exact. Zero in the classic tallies; it is what
   * makes an ambitious bid worth far more than a safe one.
   *
   * Optional because games stored before this field existed do not carry it —
   * `scoreRound` reads it as 0 so an old game never rescores differently.
   */
  bidSquaredBonus?: number;
  /** Points lost per trick of difference when you miss your bid. */
  missPenaltyPerTrick: number;
  /** House rule: on a miss, still collect a point per trick actually won. */
  alsoScoreTricksOnMiss: boolean;
}

export type ScoringPresetId = 'nl-standaard' | 'dubbel-bod' | 'mild' | 'kwadraat' | 'eigen';

export interface HouseRules {
  /**
   * The dealer bids last and may not make the bids total the number of tricks,
   * which guarantees at least one player is wrong.
   */
  dealerRestriction: boolean;
  /** If you cannot follow suit, you must play trump when you hold one. */
  mustPlayTrumpIfVoid: boolean;
  /** Bids stay hidden until everyone has bid, then reveal at once. */
  hiddenBidding: boolean;
  /**
   * In a one-card round you hold your card facing outward: everyone sees
   * everybody else's card and nobody sees their own. Live games only — with
   * physical cards the players arrange this themselves.
   */
  blindSingleCardRound: boolean;
}

export const DEFAULT_HOUSE_RULES: HouseRules = {
  dealerRestriction: true,
  mustPlayTrumpIfVoid: false,
  hiddenBidding: false,
  blindSingleCardRound: true,
};

export type RoundDirection = 'up-down' | 'up' | 'down';

export interface RoundPlanConfig {
  playerCount: number;
  /** Cap on the cards dealt in the biggest round, before the deck limit. */
  maxCards: number;
  direction: RoundDirection;
  /** On 'up-down', play the biggest round twice instead of once. */
  repeatTopRound: boolean;
  /**
   * Append one more round at the biggest hand size, played without trump.
   * Optional so a plan stored before this existed keeps its original ladder.
   */
  extraNoTrumpRound?: boolean;
  /**
   * On 'up-down', play the entire descent without trump: up with trump, down
   * without. Makes the second half of the game markedly harder, since nothing
   * can be stopped with a small trump any more.
   *
   * Optional for the same reason as above.
   */
  noTrumpOnTheWayDown?: boolean;
  /** Seat that deals round 1; the deal rotates one seat per round. */
  firstDealerSeat: number;
}

export interface PlannedRound {
  /** 1-based. */
  roundNo: number;
  cards: number;
  dealerSeat: number;
  /** Play this round without trump, regardless of what the deck allows. */
  noTrump: boolean;
}

export interface GameConfig {
  roundPlan: RoundPlanConfig;
  scoringPreset: ScoringPresetId;
  scoring: ScoringConfig;
  houseRules: HouseRules;
}

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

/** A saved profile, so stats accumulate across games. */
export interface PlayerProfile {
  id: string;
  name: string;
  emoji: string;
}

/** A seat at one particular table. */
export interface Seat {
  seat: number;
  displayName: string;
  /** Null for a one-off player who was typed in without saving a profile. */
  profileId: string | null;
}

// ---------------------------------------------------------------------------
// Scorekeeper mode (physical cards, offline)
// ---------------------------------------------------------------------------

export interface ScoreEntry {
  seat: number;
  /** Null until entered. */
  bid: number | null;
  tricksWon: number | null;
  /** Derived from bid + tricksWon via `scoreRound`; null while incomplete. */
  points: number | null;
}

export type RoundStatus = 'bidding' | 'playing' | 'done';

export interface ScoreRound {
  roundNo: number;
  cards: number;
  dealerSeat: number;
  /** Optional: recording the trump makes per-suit stats possible later. */
  trump?: TrumpSuit;
  status: RoundStatus;
  entries: ScoreEntry[];
}

export type GameStatus = 'in-progress' | 'finished';

/**
 * One scorekeeper game. This whole object is what lives in localStorage and
 * what gets PUT to the server as an idempotent snapshot.
 */
export interface ScoreGame {
  /** Client-generated uuid; the sync key. */
  id: string;
  mode: 'scorekeeper';
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
  status: GameStatus;
  config: GameConfig;
  seats: Seat[];
  rounds: ScoreRound[];
}

export interface Standing {
  seat: number;
  displayName: string;
  total: number;
  /** 1-based; tied players share a placement. */
  placement: number;
}
